/*
 * Copyright (c) 2026 Paxia AI Pty Ltd. All rights reserved.
 * This software is proprietary and confidential.
 * No part of this codebase may be copied, modified, published, distributed, sublicensed, or used without prior written permission from Paxia AI Pty Ltd.
 */

import React from 'react';

// Allowed arithmetic operators
const ARITH_OPS = ['+','-','*','/'];

/**
 * ArithmeticEditor
 * node: { id, type:'arithmetic', tokens:[ {kind:'source', ref:string}, {kind:'op', value:string}, ... ] }
 * availableSeries: [{value,label}] referencing node outputs (excludes comparison nodes)
 */
export default function ArithmeticEditor({ node, onChange, availableSeries }) {
  if (!node || node.type !== 'arithmetic') return null;
  const tokens = node.tokens || [];

  const updateTokens = (next) => onChange({ ...node, tokens: next });

  const addSource = (ref) => {
    if (!ref) return;
    // If empty OR last token is operator we can append source
    if (tokens.length === 0 || tokens[tokens.length-1].kind === 'op') {
      updateTokens([...tokens, { kind:'source', ref }]);
    }
  };
  const addOp = (op) => {
    if (!op) return;
    if (tokens.length === 0) return; // can't start with op
    if (tokens[tokens.length-1].kind === 'op') return; // no double op
    updateTokens([...tokens, { kind:'op', value: op }]);
  };
  const removeAt = (i) => {
    const next = [...tokens];
    next.splice(i,1);
    updateTokens(next);
  };
  const replaceSource = (i, ref) => {
    const next = tokens.map((t,idx)=> idx===i ? { ...t, ref } : t);
    updateTokens(next);
  };

  // Provide raw price/volume base sources plus available series
  const baseSources = ['volume','close','open','high','low'].map(f => ({ value: f, label: f }));
  const seriesOptions = [...baseSources, ...availableSeries.filter(o => !baseSources.some(b=>b.value===o.value))];

  const pretty = tokens.map(t => t.kind==='source' ? t.ref : t.value).join(' ');

  // Basic validation flags
  const errors = [];
  if (tokens.length === 0) errors.push('Empty expression');
  if (tokens[0]?.kind === 'op') errors.push('Cannot start with operator');
  if (tokens[tokens.length-1]?.kind === 'op') errors.push('Cannot end with operator');
  for (let i=1;i<tokens.length;i++) {
    if (tokens[i].kind === 'op' && tokens[i-1].kind === 'op') errors.push('Adjacent operators');
  }

  return (
    <div className="space-y-2">
      <div className="flex items-center justify-between">
        <h5 className="text-[10px] uppercase tracking-wide text-textSecondary">Arithmetic Tokens</h5>
        <button
          type="button"
          className="px-2 py-0.5 rounded bg-muted/40 text-[10px] border hover:bg-muted"
          onClick={()=>updateTokens([])}
        >Clear</button>
      </div>
      <div className="flex flex-wrap gap-1 min-h-[34px] items-center border rounded-md p-2 bg-background">
        {tokens.map((t,i) => (
          <Token
            key={i}
            token={t}
            index={i}
            replaceSource={replaceSource}
            remove={()=>removeAt(i)}
            seriesOptions={seriesOptions}
          />
        ))}
        {tokens.length === 0 && (
          <span className="text-[10px] italic text-textSecondary">(empty)</span>
        )}
      </div>
      <div className="flex flex-wrap gap-2 items-center text-[10px]">
        <div className="flex items-center gap-1">
          <select
            className="h-7 text-xs rounded-md border bg-background"
            onChange={(e)=>{ addSource(e.target.value); e.target.value=''; }}
            defaultValue=""
          >
            <option value="" disabled>Add source</option>
            {seriesOptions.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
          </select>
        </div>
        <div className="flex items-center gap-1">
          {ARITH_OPS.map(op => (
            <button key={op} type="button" className="px-2 py-0.5 rounded border bg-muted/40 hover:bg-muted" onClick={()=>addOp(op)}>{op}</button>
          ))}
        </div>
        <div className="ml-auto text-[10px] text-textSecondary truncate max-w-[50%]" title={pretty}>{pretty || '(no expression)'}</div>
      </div>
      {errors.length>0 && (
        <ul className="space-y-0.5">
          {Array.from(new Set(errors)).map((e,i)=>(<li key={i} className="text-[10px] text-red-600 flex items-start gap-1"><span>•</span><span>{e}</span></li>))}
        </ul>
      )}
    </div>
  );
}

function Token({ token, index, remove, replaceSource, seriesOptions }) {
  if (token.kind === 'op') {
    return (
      <span className="px-1.5 py-0.5 rounded bg-primary/10 text-primary border border-primary/40 text-[10px] flex items-center gap-1">
        {token.value}
        <button type="button" className="text-red-500 hover:text-red-600" onClick={remove} title="Remove">×</button>
      </span>
    );
  }
  return (
    <span className="flex items-center gap-1 px-1 py-0.5 rounded bg-muted/30 border text-[10px]">
      <select
        value={token.ref}
        onChange={(e)=>replaceSource(index, e.target.value)}
        className="bg-transparent text-[10px]"
      >
        {seriesOptions.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
      </select>
      <input
        type="number"
        min={0}
        title="Lag bars"
        value={token.lag || ''}
        onChange={(e)=>{
          const v = e.target.value === '' ? undefined : Math.max(0, parseInt(e.target.value,10)||0);
          replaceSource(index, token.ref + (v ? '' : '')); // reuse replaceSource to trigger parent update
          // Mutation pattern: we rely on parent map; safer to dispatch explicit update via custom event
        }}
        className="w-10 h-5 text-[10px] rounded border bg-background px-1"
        placeholder="lag"
      />
      <button type="button" className="text-red-500 hover:text-red-600" onClick={remove}>×</button>
    </span>
  );
}
